import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle, ChevronRight, Loader2, GripVertical, FileText, ArrowRight, Info } from "lucide-react";
import { Button } from "./ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ========== Input Components ==========

const ShortTextInput = ({ value, onChange, onSubmit }) => (
  <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
    onKeyDown={(e) => e.key === 'Enter' && onSubmit()}
    placeholder="Type your answer..."
    inputMode="text"
    className="w-full rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#6B5E7C] px-4 py-4 md:py-3 text-lg md:text-base focus:outline-none focus:border-[#E91E8C]/50 focus:ring-1 focus:ring-[#E91E8C]/30"
    data-testid="station-qa-input" autoFocus />
);

const LongTextInput = ({ value, onChange, inputRef }) => (
  <textarea ref={inputRef} value={value} onChange={(e) => onChange(e.target.value)}
    placeholder="Type your answer..." rows={4}
    className="w-full rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#6B5E7C] p-4 text-lg md:text-base focus:outline-none focus:border-[#E91E8C]/50 focus:ring-1 focus:ring-[#E91E8C]/30 resize-none min-h-[120px] md:min-h-[96px]"
    data-testid="station-qa-input" />
);

const TrueFalseInput = ({ value, onChange }) => (
  <div className="flex gap-3" data-testid="station-qa-input">
    {["True", "False"].map((opt) => (
      <button key={opt} type="button" onClick={() => onChange(opt)}
        className={`flex-1 min-h-[64px] md:min-h-[56px] py-5 md:py-4 rounded-xl text-xl md:text-lg font-semibold transition-all border active:scale-[0.98] touch-manipulation ${
          value === opt
            ? opt === "True" ? "bg-emerald-500/20 border-emerald-500 text-emerald-400" : "bg-red-500/20 border-red-500 text-red-400"
            : "bg-white/5 border-white/10 text-[#E2D9F3] hover:border-white/30"
        }`}
        data-testid={`station-qa-tf-${opt.toLowerCase()}`}>
        {opt}
      </button>
    ))}
  </div>
);

const ScaleInput = ({ value, onChange, min = 1, max = 5 }) => {
  const range = [];
  for (let i = min; i <= max; i++) range.push(i);
  return (
    <div className="flex gap-2 sm:gap-3 justify-center flex-wrap" data-testid="station-qa-input">
      {range.map((n) => (
        <button key={n} type="button" onClick={() => onChange(String(n))}
          className={`w-14 h-14 md:w-12 md:h-12 rounded-xl text-xl md:text-lg font-bold transition-all border active:scale-95 touch-manipulation ${
            value === String(n) ? "border-[#E91E8C] text-white" : "bg-white/5 border-white/10 text-[#E2D9F3] hover:border-white/30"
          }`}
          style={value === String(n) ? { background: 'var(--theme-gradient, linear-gradient(135deg, #E91E8C 0%, #8B5CF6 100%))' } : {}}
          data-testid={`station-qa-scale-${n}`}>
          {n}
        </button>
      ))}
    </div>
  );
};

const RankingItemsInput = ({ value, onChange, items }) => {
  const [orderedItems, setOrderedItems] = useState(value ? value.split(" > ") : [...items]);
  useEffect(() => { onChange(orderedItems.join(" > ")); }, [orderedItems]);
  return (
    <div className="space-y-2" data-testid="station-qa-input">
      <p className="text-xs text-[#6B5E7C] mb-2">Tap the arrows to reorder (top = highest rank)</p>
      {orderedItems.map((item, i) => (
        <div key={item + i} className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
          <span className="text-[#E91E8C] font-bold text-base w-6">{i + 1}</span>
          <GripVertical className="w-4 h-4 text-[#6B5E7C] hidden sm:block" />
          <span className="text-white flex-1 leading-snug">{item}</span>
          <div className="flex gap-2">
            <button type="button"
              onClick={() => { if (i === 0) return; const a = [...orderedItems]; [a[i-1],a[i]]=[a[i],a[i-1]]; setOrderedItems(a); }}
              disabled={i === 0}
              aria-label={`Move ${item} up`}
              className="w-11 h-11 rounded-lg bg-white/10 text-white text-lg flex items-center justify-center hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed active:scale-95 touch-manipulation"
              data-testid={`station-qa-rank-up-${i}`}>↑</button>
            <button type="button"
              onClick={() => { if (i >= orderedItems.length - 1) return; const a = [...orderedItems]; [a[i],a[i+1]]=[a[i+1],a[i]]; setOrderedItems(a); }}
              disabled={i >= orderedItems.length - 1}
              aria-label={`Move ${item} down`}
              className="w-11 h-11 rounded-lg bg-white/10 text-white text-lg flex items-center justify-center hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed active:scale-95 touch-manipulation"
              data-testid={`station-qa-rank-down-${i}`}>↓</button>
          </div>
        </div>
      ))}
    </div>
  );
};

const FillBlankInput = ({ value, onChange, template, onSubmit }) => {
  const parts = template ? template.split("___") : ["", ""];
  return (
    <div className="space-y-3" data-testid="station-qa-input">
      <div className="text-[#E2D9F3] text-xl md:text-lg leading-relaxed">
        {parts[0]}
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && onSubmit()}
          className="inline-block mx-1 px-3 py-2 md:py-1 rounded-lg bg-white/10 border-b-2 border-[#E91E8C] text-white text-xl md:text-lg min-w-[140px] focus:outline-none focus:bg-white/15"
          placeholder="..." autoFocus />
        {parts.length > 1 && parts[1]}
      </div>
    </div>
  );
};

// Render the right input for a question item (question or part)
const renderInput = (item, answer, setAnswer, handleSubmit, inputRef) => {
  const qType = item.question_type || "long_text";
  switch (qType) {
    case "short_text": return <ShortTextInput value={answer} onChange={setAnswer} onSubmit={handleSubmit} />;
    case "true_false": return <TrueFalseInput value={answer} onChange={setAnswer} />;
    case "ranking":
      if (item.ranking_items?.length > 0) return <RankingItemsInput value={answer} onChange={setAnswer} items={item.ranking_items} />;
      return <ScaleInput value={answer} onChange={setAnswer} min={item.scale_min || 1} max={item.scale_max || 5} />;
    case "fill_blank": return <FillBlankInput value={answer} onChange={setAnswer} template={item.blank_template} onSubmit={handleSubmit} />;
    default: return <LongTextInput value={answer} onChange={setAnswer} inputRef={inputRef} />;
  }
};

// ========== Flatten questions into answerable steps ==========
// Each step is: { questionId, partId (optional), text, label, parentTitle, ...typeFields }
function flattenQuestions(questions) {
  const steps = [];
  for (const q of questions) {
    if (q.parts && q.parts.length > 0) {
      for (const p of q.parts) {
        steps.push({
          questionId: q.id,
          partId: p.id,
          answerId: `${q.id}_${p.id}`,
          text: p.text,
          label: p.label || "",
          parentTitle: q.text,
          question_type: p.question_type || "long_text",
          ranking_items: p.ranking_items || [],
          blank_template: p.blank_template || "",
          scale_min: p.scale_min ?? 1,
          scale_max: p.scale_max ?? 5,
          scope: p.scope || q.scope || "individual",
        });
      }
    } else {
      steps.push({
        questionId: q.id,
        partId: null,
        answerId: q.id,
        text: q.text,
        label: null,
        parentTitle: null,
        question_type: q.question_type || "long_text",
        ranking_items: q.ranking_items || [],
        blank_template: q.blank_template || "",
        scale_min: q.scale_min ?? 1,
        scale_max: q.scale_max ?? 5,
        scope: q.scope || "individual",
      });
    }
  }
  return steps;
}

// ========== Main Component ==========

const StationQA = ({ sessionId, stationId, questions, deviceId, instructions, isTeamLead = false }) => {
  const [phase, setPhase] = useState(instructions?.trim() ? "instructions" : "questions");
  const [currentStepIdx, setCurrentStepIdx] = useState(0);
  const [answer, setAnswer] = useState("");
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const inputRef = useRef(null);

  const allSteps = flattenQuestions(questions || []);
  // Filter steps: instruction steps are always shown to everyone; team lead sees all,
  // non-lead sees only individual answerable questions.
  const steps = allSteps.filter(s => s.question_type === "instruction" || isTeamLead || s.scope !== "team");
  const totalSteps = steps.length;
  // Answerable steps only (instruction steps need no input and aren't counted)
  const answerableSteps = steps.filter(s => s.question_type !== "instruction");
  const totalAnswerable = answerableSteps.length;
  // Count of team-only answerable questions (for display)
  const teamOnlyCount = allSteps.filter(s => s.scope === "team" && s.question_type !== "instruction").length;

  // Load existing progress
  useEffect(() => {
    const loadProgress = async () => {
      try {
        const res = await axios.get(`${API}/sessions/${sessionId}/stations/${stationId}/progress/${deviceId}`);
        const answered = res.data.answered || [];
        if (answered.length > 0) {
          const map = {};
          answered.forEach(a => { map[a.question_id] = a.answer_text; });
          setAnswers(map);
          // Find first unanswered answerable step (skip instruction steps)
          const firstUnanswered = steps.findIndex(s => s.question_type !== "instruction" && !map[s.answerId]);
          if (firstUnanswered === -1) {
            setIsComplete(true);
            setPhase("questions");
          } else {
            setCurrentStepIdx(firstUnanswered);
            setPhase("questions");
          }
        }
      } catch {}
    };
    if (sessionId && stationId && deviceId && steps.length) loadProgress();
  }, [sessionId, stationId, deviceId]);

  useEffect(() => {
    if (phase === "questions" && !isComplete) setTimeout(() => inputRef.current?.focus(), 300);
  }, [currentStepIdx, isComplete, phase]);

  const currentStep = steps[currentStepIdx];
  const isLastStep = currentStepIdx === totalSteps - 1;

  const submitAnswer = async (directValue) => {
    const val = (directValue || answer).trim();
    if (!val) { toast.error("Please provide your answer"); return; }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/sessions/${sessionId}/stations/${stationId}/answers`, {
        device_id: deviceId,
        question_id: currentStep.answerId,
        answer_text: val,
      });
      const newAnswers = { ...answers, [currentStep.answerId]: val };
      setAnswers(newAnswers);
      setAnswer("");
      if (isLastStep) {
        setIsComplete(true);
        toast.success("All questions answered!");
      } else {
        setCurrentStepIdx(prev => prev + 1);
      }
    } catch {
      toast.error("Failed to submit answer");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (e) => { e?.preventDefault(); submitAnswer(); };

  const handleTFChange = (val) => {
    setAnswer(val);
    setTimeout(() => submitAnswer(val), 300);
  };

  // Advance past an instruction step (no answer saved)
  const goNext = () => {
    if (isLastStep) {
      setIsComplete(true);
    } else {
      setCurrentStepIdx(prev => prev + 1);
    }
    setAnswer("");
  };

  if (!questions?.length && !instructions?.trim()) return null;

  // ========== Instructions Phase ==========
  if (phase === "instructions" && instructions?.trim()) {
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="space-y-4" data-testid="station-instructions-phase">
        <div className="flex items-center gap-2 mb-3">
          <FileText className="w-5 h-5" style={{ color: 'var(--theme-accent, #E91E8C)' }} />
          <h3 className="text-lg font-semibold text-white">Instructions</h3>
        </div>
        <div className="glass-light rounded-xl p-5">
          <p className="text-white text-base leading-relaxed whitespace-pre-wrap">{instructions}</p>
        </div>
        {totalSteps > 0 && (
          <Button onClick={() => setPhase("questions")}
            className="w-full btn-primary-gradient py-4 md:py-3 text-lg md:text-base font-semibold min-h-[56px] md:min-h-[48px] touch-manipulation"
            data-testid="continue-to-questions-btn">
            <ArrowRight className="w-5 h-5 mr-2" />
            Continue to Questions ({totalSteps})
          </Button>
        )}
      </motion.div>
    );
  }

  // ========== Completion Phase ==========
  if (isComplete || totalSteps === 0) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="space-y-4" data-testid="station-qa-complete">
        <div className="text-center py-6">
          <CheckCircle className="w-14 h-14 mx-auto mb-3" style={{ color: 'var(--theme-accent, #00E676)' }} />
          <h3 className="text-xl font-bold text-white mb-1">All Done!</h3>
          <p className="text-[#E2D9F3] text-sm">
            {totalAnswerable > 0 ? `${totalAnswerable} answer${totalAnswerable !== 1 ? 's' : ''} submitted` : "Instructions reviewed"}
          </p>
        </div>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {answerableSteps.map((step, i) => (
            <div key={step.answerId} className="glass-light rounded-xl p-3">
              <p className="text-xs text-[#6B5E7C] mb-1">
                {step.parentTitle ? `${step.parentTitle} (${step.label})` : `Q${i + 1}`}: {step.text}
              </p>
              <p className="text-white text-sm">{answers[step.answerId]}</p>
            </div>
          ))}
        </div>
      </motion.div>
    );
  }

  // ========== Question Phase ==========
  const qType = currentStep.question_type || "long_text";
  const isTeamQ = currentStep.scope === "team";
  const isInstruction = qType === "instruction";

  return (
    <div className="space-y-4" data-testid="station-qa">
      {/* Progress bar (answerable questions only) */}
      {totalAnswerable > 0 && (
        <div className="flex items-center gap-2 mb-2">
          <div className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden">
            <motion.div className="h-full rounded-full"
              style={{ background: 'var(--theme-gradient, linear-gradient(135deg, #E91E8C 0%, #8B5CF6 100%))' }}
              initial={{ width: 0 }}
              animate={{ width: `${(Object.keys(answers).length / totalAnswerable) * 100}%` }}
              transition={{ duration: 0.4 }} />
          </div>
          <span className="text-xs text-[#E2D9F3] font-mono whitespace-nowrap">
            {Object.keys(answers).length}/{totalAnswerable}
          </span>
        </div>
      )}

      {/* Non-lead info about team questions */}
      {!isTeamLead && teamOnlyCount > 0 && (
        <div className="text-xs text-[#6B5E7C] bg-white/5 rounded-lg px-3 py-2 text-center">
          {teamOnlyCount} team question{teamOnlyCount > 1 ? 's' : ''} will be answered by your Team Lead
        </div>
      )}

      <AnimatePresence mode="wait">
        <motion.div key={currentStepIdx}
          initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.25 }}>

          {/* Show parent title for sub-questions */}
          {currentStep.parentTitle && (
            <div className="mb-2 pb-2 border-b border-white/10">
              <p className="text-sm text-[#6B5E7C] font-medium">{currentStep.parentTitle}</p>
            </div>
          )}

          {isInstruction ? (
            /* ===== Instruction / Info step (no input, just Continue) ===== */
            <div data-testid="station-qa-instruction">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(255,255,255,0.08)' }}>
                  <Info className="w-5 h-5" style={{ color: 'var(--theme-accent, #E91E8C)' }} />
                </div>
                <p className="text-white text-base leading-relaxed pt-1 whitespace-pre-wrap flex-1">{currentStep.text}</p>
              </div>
              <Button type="button" onClick={goNext}
                className="w-full btn-primary-gradient py-4 md:py-3 text-lg md:text-base font-semibold min-h-[56px] md:min-h-[48px] touch-manipulation"
                data-testid="station-qa-continue">
                {isLastStep ? <CheckCircle className="w-5 h-5 mr-2" /> : <ArrowRight className="w-5 h-5 mr-2" />}
                {isLastStep ? "Finish" : "Continue"}
              </Button>
            </div>
          ) : (
            <>
              <div className="flex items-start gap-3 mb-4">
                <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-sm"
                  style={{ background: 'var(--theme-gradient, linear-gradient(135deg, #E91E8C 0%, #8B5CF6 100%))' }}>
                  {currentStep.label || (currentStepIdx + 1)}
                </div>
                <div className="flex-1">
                  <p className="text-white text-lg font-medium leading-snug pt-1">{currentStep.text}</p>
                  {isTeamQ && (
                    <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#8B5CF6]/20 text-[#8B5CF6]">
                      Team Question
                    </span>
                  )}
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-3">
                {qType === "true_false" ? (
                  <TrueFalseInput value={answer} onChange={handleTFChange} />
                ) : (
                  renderInput(currentStep, answer, setAnswer, handleSubmit, inputRef)
                )}

                {qType !== "true_false" && (
                  <Button type="submit" disabled={isSubmitting || !answer.trim()}
                    className="w-full btn-primary-gradient py-4 md:py-3 text-lg md:text-base font-semibold min-h-[56px] md:min-h-[48px] touch-manipulation"
                    data-testid="station-qa-submit">
                    {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> :
                      isLastStep ? <CheckCircle className="w-5 h-5 mr-2" /> : <ChevronRight className="w-5 h-5 mr-2" />}
                    {isSubmitting ? "Submitting..." : isLastStep ? "Submit Final Answer" : "Next"}
                  </Button>
                )}
              </form>
            </>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default StationQA;
